import '@mdi/font/css/materialdesignicons.css';
import Vuetify from 'vuetify/lib';
import './theme/index.js';
import standardTheme from './theme/standardTheme.js';
import './language/index.js';
import './storage/index.js';
import Theme from './theme/Theme.js';
import Language from './language/Language.js';
import Storage from './storage/Storage.js';
import { installSupplyThemeConfigurationTokens } from './theme/themeConfiguration.js';

let $wtgMaterialUi;
var AppearanceType;
(function (AppearanceType) {
  AppearanceType["Light"] = "light";
  AppearanceType["Dark"] = "dark";
  AppearanceType["Muted"] = "muted";
})(AppearanceType || (AppearanceType = {}));
var CurrentNotificationType;
(function (CurrentNotificationType) {
  CurrentNotificationType["Click"] = "click";
  CurrentNotificationType["MouseOver"] = "mouseover";
  CurrentNotificationType["MouseOut"] = "mouseout";
})(CurrentNotificationType || (CurrentNotificationType = {}));
var NotificationType;
(function (NotificationType) {
  NotificationType[NotificationType["None"] = 0] = "None";
  NotificationType[NotificationType["Information"] = 1] = "Information";
  NotificationType[NotificationType["Warning"] = 2] = "Warning";
  NotificationType[NotificationType["MessageError"] = 3] = "MessageError";
  NotificationType[NotificationType["Error"] = 4] = "Error";
})(NotificationType || (NotificationType = {}));
class WtgMaterialUi {
  constructor(options = {}) {
    var _a;
    this._currentNotification = {};
    this.options = options;
    this._appearance = AppearanceType.Light;
    this._baseTheme = new Theme(options.theme, standardTheme);
    this._theme = new Theme(options.themeConfiguration, this._baseTheme);
    this._vuetify = new Vuetify({
      lang: Language.toVuetifyLangOptions(options.language),
      theme: this._theme.toVuetifyOptions(),
      icons: this._theme.icons.toVuetifyOptions()
    });
    this._theme.install();
    this.appearance = options.dark ? AppearanceType.Dark : AppearanceType.Light;
    this.language = new Language(this._vuetify, options.language);
    this.storage = new Storage(options.storage);
    this.frameworkVersionNumber = options.frameworkVersion;
    this.supplyBetaComponents = (_a = options.supplyBetaComponents) !== null && _a !== void 0 ? _a : false;
    $wtgMaterialUi = this;
  }
  get currentNotification() {
    return this._currentNotification;
  }
  set currentNotification(value) {
    this._currentNotification = value;
  }
  get theme() {
    return this._theme;
  }
  get baseTheme() {
    return this._baseTheme;
  }
  get dark() {
    return this._appearance === AppearanceType.Dark;
  }
  get appearance() {
    return this._appearance;
  }
  set appearance(value) {
    this._appearance = value;
    this._vuetify.framework.theme.dark = this.dark;
    const html = document.querySelector('html');
    if (html) {
      switch (this.appearance) {
        case AppearanceType.Dark:
          html.setAttribute('data-theme', 'theme-dark');
          break;
        case AppearanceType.Muted:
          html.setAttribute('data-theme', 'theme-muted');
          break;
        default:
          html.setAttribute('data-theme', '');
          break;
      }
    }
    this.installVuetifyColors();
  }
  get application() {
    return this._vuetify.framework.application;
  }
  get viewportHeightOffset() {
    var _a, _b;
    return this.application.top + this.application.footer + (this.application.insetFooter > 0 ? this.application.insetFooter + 8 : 0) + ((_b = (_a = this.options) === null || _a === void 0 ? void 0 : _a.viewportHeightOffset) !== null && _b !== void 0 ? _b : 0);
  }
  get breakpoint() {
    return this._vuetify.framework.breakpoint;
  }
  get colors() {
    var _a;
    switch (this.appearance) {
      case AppearanceType.Dark:
        return this.theme.colors.dark;
      case AppearanceType.Muted:
        return (_a = this.theme.colors.muted) !== null && _a !== void 0 ? _a : this.theme.colors.light;
      default:
        return this.theme.colors.light;
    }
  }
  get icons() {
    return this.theme.icons;
  }
  get frameworkVersion() {
    return this.frameworkVersionNumber ? this.frameworkVersionNumber : 0;
  }
  set frameworkVersion(newValue) {
    this.frameworkVersionNumber = newValue;
  }
  get supplyVersion() {
    if (this.frameworkVersion >= 8) {
      return 1.2;
    } else if (this.frameworkVersion >= 7) {
      return 1.1;
    } else if (this.frameworkVersion >= 6) {
      return 1.0;
    } else {
      return this.theme.supplyVersion;
    }
  }
  get supplySupported() {
    return this._theme.supplyVersion > 0 || this.frameworkVersion >= 5;
  }
  get supplyFramework() {
    return this.supplySupported;
  }
  get supplyMandatoryErrorColor() {
    return this.supplySupported;
  }
  resetOptions() {
    this.appearance = this.options.dark ? AppearanceType.Dark : AppearanceType.Light;
  }
  installTheme(options, baseTheme) {
    this._theme.uninstall();
    this._theme = new Theme(options, baseTheme !== null && baseTheme !== void 0 ? baseTheme : standardTheme);
    this.installVuetifyColors();
    this.installIcons(this._theme.icons.toVuetifyOptions().values);
    this._theme.install();
  }
  installVuetifyColors() {
    const colors = this._theme.colors.toVuetifyOptions();
    const lightColors = this._appearance === AppearanceType.Muted ? colors.muted : colors.light;
    for (const color in lightColors) {
      this._vuetify.framework.theme.themes.light[color] = lightColors[color];
    }
    for (const color in colors.dark) {
      this._vuetify.framework.theme.themes.dark[color] = colors.dark[color];
    }
    installSupplyThemeConfigurationTokens(this);
  }
  installIcons(icons) {
    for (const [iconName, icon] of Object.entries(icons)) {
      if (icon) this._vuetify.framework.icons.values[iconName] = icon;
    }
  }
  static install(Vue, args = {}) {
    Vue.mixin({
      beforeCreate() {
        const options = this.$options;
        if (options.wtgMaterialUi) {
          options.vuetify = options.wtgMaterialUi._vuetify;
          this.$wtgMaterialUi = Vue.observable(options.wtgMaterialUi);
        } else {
          this.$wtgMaterialUi = options.parent && options.parent.$wtgMaterialUi || $wtgMaterialUi || ($wtgMaterialUi = new WtgMaterialUi());
        }
      }
    });
    Vue.use(Vuetify);
    (function registerComponents(components) {
      for (const key in components) {
        if (key.startsWith('Wtg')) {
          const component = components[key];
          Vue.component(key, component);
        }
      }
    })(args.components || {});
  }
}

export { $wtgMaterialUi, AppearanceType, CurrentNotificationType, NotificationType, WtgMaterialUi as default };
